import { DrawerActions } from '@react-navigation/native';
import { Stack, useNavigation, useRouter } from 'expo-router';
import React, { useMemo } from 'react';
import { ActivityIndicator, FlatList, Pressable, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { IconSymbol } from '@/components/ui/IconSymbol';
import AppStyles from '@/constants/Styles';
import { useAuth } from '@/hooks/useAutoLogin';
import { useBackgroundBattlePlans } from '@/hooks/useBackgroundFetch';
import { useThemeColor } from '@/hooks/useThemeColor';
import { useCampaignStore } from '@/stores/campaignStore';
import { BattlePlan, Engagement } from '@/types/engagement';

interface BattlePlanSelectorProps {
  routeType: 'canvass' | 'phone' | 'telling' | 'gotv';
  onBattlePlanSelected?: (battlePlan: BattlePlan) => void;
  title?: string;
  battlePlanTypeFilter?: string;
}

export default function BattlePlanSelector({ routeType, onBattlePlanSelected, title, battlePlanTypeFilter }: BattlePlanSelectorProps) {
  const navigation = useNavigation();
  const router = useRouter();
  const { isLoading: authLoading } = useAuth();
  const insets = useSafeAreaInsets();
  
  // Theme-aware colors
  const backgroundColor = useThemeColor({}, 'background');
  const secondaryTextColor = useThemeColor({}, 'secondaryText');
  const iconColor = useThemeColor({}, 'icon');
  const cardBackgroundColor = useThemeColor({}, 'cardBackground');
  const borderColor = useThemeColor({}, 'border');
  const tintColor = useThemeColor({}, 'tint');
  
  // Progress bar colors from theme
  const progressHighColor = useThemeColor({}, 'progressHigh');
  const progressMediumColor = useThemeColor({}, 'progressMedium');
  const errorColor = useThemeColor({}, 'errorText');
  
  // Use background fetch hook for battle plans
  const { data: allBattlePlans, loading, error } = useBackgroundBattlePlans();
  
  // Filter battle plans by type if filter is provided and sort alphabetically
  const battlePlans = useMemo(() => {
    const filtered = battlePlanTypeFilter 
      ? allBattlePlans?.filter((bp: BattlePlan) => {
          const filterTypes = battlePlanTypeFilter.split(',').map(type => type.trim());
          return filterTypes.includes(bp.battle_plan_type);
        }) || []
      : allBattlePlans || [];
    
    // Sort alphabetically by battle_plan_name
    return filtered.sort((a: BattlePlan, b: BattlePlan) => {
      return a.battle_plan_name.localeCompare(b.battle_plan_name);
    });
  }, [allBattlePlans, battlePlanTypeFilter]);
  
  // Get store access for engagement data
  const getEngagements = useCampaignStore(state => state.getEngagements)

  // Progress bar colors (moved to top level to avoid conditional hook calls)
  const progressBackgroundColor = useThemeColor({}, 'progressBackground');

  // Progress bar for individual battle plan cards (segmented by engagement categories)
  const renderBattlePlanProgressBar = (battlePlan: BattlePlan) => {
    // Get engagement data to show address counts
    const engagements: Engagement[] | null = getEngagements(battlePlan.battle_plan_id);
    
    // Hide progress bar if no engagement data is available
    if (!engagements || engagements.length === 0) {
      return null;
    }
    
    // Calculate progress for each engagement and categorize them
    const engagementCategories = engagements.map((engagement: Engagement) => {
      const engagementProgress = engagement.address_count > 0 ? 
        ((engagement.addresses_visited + engagement.addresses_not_in) / engagement.address_count) * 100 : 0;
      
      let category: 'green' | 'orange' | 'red' | 'grey';
      if (engagementProgress >= 80) {
        category = 'green';
      } else if (engagementProgress >= 50) {
        category = 'orange';
      } else if (engagementProgress > 0) {
        category = 'red';
      } else {
        category = 'grey';
      }
      
      return { engagement, progress: engagementProgress, category };
    });
    
    // Count engagements in each category
    const greenCount = engagementCategories.filter(e => e.category === 'green').length;
    const orangeCount = engagementCategories.filter(e => e.category === 'orange').length;
    const redCount = engagementCategories.filter(e => e.category === 'red').length;
    
    const totalEngagements = engagements.length;
    
    // Calculate percentages for each segment
    const greenPercentage = (greenCount / totalEngagements) * 100;
    const orangePercentage = (orangeCount / totalEngagements) * 100;
    const redPercentage = (redCount / totalEngagements) * 100;

    return (
      <View style={AppStyles.progressBarContainer}>
        <View style={[AppStyles.progressBarBackground, { backgroundColor: progressBackgroundColor }]}>
          {/* Green segment */}
          {greenPercentage > 0 && (
            <View
              style={[
                AppStyles.progressBarSegment,
                {
                  backgroundColor: progressHighColor,
                  width: `${greenPercentage}%`,
                }
              ]}
            />
          )}
          {/* Orange segment */}
          {orangePercentage > 0 && (
            <View
              style={[
                AppStyles.progressBarSegment,
                {
                  backgroundColor: progressMediumColor,
                  width: `${orangePercentage}%`,
                }
              ]}
            />
          )}
          {/* Red segment */}
          {redPercentage > 0 && (
            <View
              style={[
                AppStyles.progressBarSegment,
                {
                  backgroundColor: errorColor,
                  width: `${redPercentage}%`,
                }
              ]}
            />
          )}
        </View>
      </View>
    );
  };

  const handleSelect = (battlePlan: BattlePlan) => {
    if (onBattlePlanSelected) {
      onBattlePlanSelected(battlePlan);
    } else {
      // Navigate to the battle plan route
      console.log('Navigating to battle plan route', routeType, battlePlan.battle_plan_id);
      router.replace(`./${routeType}/${battlePlan.battle_plan_id}`);
    }
  };

  if (authLoading) {
    return (
      <ThemedView style={[AppStyles.container, { backgroundColor }]}>
        <ActivityIndicator size="large" color={iconColor} />
      </ThemedView>
    );
  }

  if (error) {
    return (
      <ThemedView style={[AppStyles.container, { backgroundColor }]}>
        <ThemedText>Error loading battle plans: {String(error)}</ThemedText>
      </ThemedView>
    );
  }

  return (
    <ThemedView style={[AppStyles.container, { backgroundColor }]}>
      <Stack.Screen
        options={{
          headerTitle: () => (
            <View style={AppStyles.headerContainer}>
              <ThemedText style={AppStyles.headerText}>{title || 'Battle Plans'}</ThemedText>
            </View>
          ),
          headerLeft: () => (
            <TouchableOpacity onPress={() => navigation.dispatch(DrawerActions.toggleDrawer())} style={{ paddingLeft: 15 }}>
              <IconSymbol name="chevron.right" size={28} color={tintColor} />
            </TouchableOpacity>
          ),
          headerBackVisible: false,
        }}
      />
      
      {loading && <ActivityIndicator size="large" color={iconColor} />}
      {error && <ThemedText style={AppStyles.errorText}>{error}</ThemedText>}
      
      
      {!loading && !error && (!battlePlans || battlePlans.length === 0) && (
        <View style={AppStyles.emptyContainer}>
          <ThemedText style={AppStyles.noDataText}>
            {routeType === 'telling' ? 'No polling stations found' : 'No canvassing plans found'}
          </ThemedText>
        </View>
      )}
      {!loading && !error && battlePlans && battlePlans.length > 0 && (
        <FlatList
          data={battlePlans}
          keyExtractor={(item) => item.battle_plan_id}
          renderItem={({ item }) => {

            return (
              <Pressable 
                style={[
                  AppStyles.listItemLeftAligned, 
                  { backgroundColor: cardBackgroundColor, borderBottomColor: borderColor }
                ]} 
                onPress={() => handleSelect(item)}
              >
                <View style={{ width: '100%' }}>
                  <ThemedText style={AppStyles.headerText}>{item.battle_plan_name}</ThemedText>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                    <ThemedText style={{ color: secondaryTextColor, fontSize: 14 }}>
                      {routeType === 'gotv' || routeType === 'telling' ? 'Polling stations' : 'Open walk routes'}: {item.open_engagement_count}
                    </ThemedText>
                  </View>
                  {renderBattlePlanProgressBar(item)}
                </View>
              </Pressable>
            );
          }}
          contentContainerStyle={{ paddingBottom: insets.bottom }}
        />
      )}
    </ThemedView>
  );
} 